import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, ButtonBase, Checkbox, IconButton, Stack, TableCell, TableRow, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { palette } from '../../../theme';
import BasicTable, { NUMERIC_CELL_SX } from '../../../components/BasicTable/BasicTable';
import ListToolbar from '../../../components/ListToolbar/ListToolbar';
import RowActionsMenu from '../../../components/RowActionsMenu/RowActionsMenu';
import FilteredEmptyState from '../../../components/FilteredEmptyState';
import StatusChip from '../../../components/StatusChip';
import { backState } from '../../../components/BackLink';
import GenericInfoModal from '../../../components/GenericInfoModal';
import CancelInvoiceModal from './CancelInvoiceModal';
import IssueInvoicesDialog from './Invoices/IssueInvoicesDialog';
import SendInvoicesDialog from './Invoices/SendInvoicesDialog';
import { ITableHeadings } from '../../../common/interfaces/common.interface';
import { MESSAGE_TYPE } from '../../../common/constants/common.constants';
import { useListUrlState } from '../../../utilities/hooks/useListUrlState';
import { invoiceStateOf } from '../../../utilities/helper/invoiceState.helper';
import { createNotification } from '../../../utilities/helper/notification.helper';
import { DEFAULT_CURRENCY, formatMoney } from '../../../utilities/helper/money.helper';
import { downloadBlob, downloadFromPresignedUrl } from '../../../utilities/helper/download.helper';
import { daysBetween, formatSerbianDate, parseMonthKey, toLocalDateString } from '../../../utilities/helper/date.helper';
import {
  InvoicePaymentStatusEnum,
  InvoicePdfStatusEnum,
  InvoicesSortKeyEnum,
  InvoiceStateFilterEnum,
  InvoiceStatusEnum
} from '../../../common/enums/invoicing.enum';
import { SortOrderEnum } from '../../../common/enums/common.enum';
import { InvoiceDto, InvoiceMonthCountsDto } from '../../../common/interfaces/invoicing.interface';
import { deleteInvoiceDraft, downloadInvoicesZip, getInvoicePdfUrl, getInvoices } from '../../../api/invoicing.api';
import { getGroupsWithLocationName } from '../../../api/kindergarten.api';
import { FINANCE } from '../../../common/constants/routes.constants';
import { useLatestRequest } from '../../../utilities/hooks/useLatestRequest';

export const INVOICES_TABLE_HEADINGS: ITableHeadings[] = [
  { id: 0, value: '', label: '', sortable: false, width: 48 },
  { id: 1, value: '', label: 'invoices.columns.invoice', sortable: true, sortKey: InvoicesSortKeyEnum.NUMBER, width: '16%' },
  { id: 2, value: '', label: 'common.child', sortable: true, sortKey: InvoicesSortKeyEnum.CHILD, width: '24%' },
  { id: 3, value: '', label: 'invoices.columns.amount', sortable: true, sortKey: InvoicesSortKeyEnum.TOTAL, align: 'right' },
  { id: 4, value: '', label: 'invoices.columns.remaining', sortable: true, sortKey: InvoicesSortKeyEnum.REMAINING, align: 'right' },
  { id: 5, value: '', label: 'invoices.columns.state', sortable: false },
  { id: 6, value: '', label: 'invoices.columns.dueDate', sortable: true, sortKey: InvoicesSortKeyEnum.DUE_DATE },
  { id: 7, value: '', label: '', sortable: false, width: 56 }
];

/** The state chips over the list, and which month count each shows. */
const STATE_CHIPS: { value: InvoiceStateFilterEnum | undefined; count: keyof InvoiceMonthCountsDto }[] = [
  { value: undefined, count: 'all' },
  { value: InvoiceStateFilterEnum.DRAFT, count: 'draft' },
  { value: InvoiceStateFilterEnum.ISSUED_NOT_SENT, count: 'issuedNotSent' },
  { value: InvoiceStateFilterEnum.UNPAID, count: 'unpaid' },
  { value: InvoiceStateFilterEnum.PAID, count: 'paid' }
];

const FILTER = { state: 'state', group: 'group' };

const remainingOf = (invoice: InvoiceDto): number => Math.max(invoice.totalGross - invoice.paidAmount, 0);
const isIssued = (invoice: InvoiceDto) => invoice.status === InvoiceStatusEnum.ISSUED;
const isSendable = (invoice: InvoiceDto) => isIssued(invoice) && !!invoice.payerEmail;

interface InvoicesTabProps {
  kindergartenId: string;
  /** `YYYY-MM`: the list is always one month. */
  month: string;
  /** The month's counts, for the numbers on the state chips. */
  counts: InvoiceMonthCountsDto | null;
  /** Something changed that the step cards above should re-count. */
  onChanged: () => void;
}

/**
 * One month's invoices, in one state each.
 *
 * The state is the server's (`state` on each row): payment before delivery, so a paid
 * invoice reads "paid" whether or not its mail went out. Selecting rows brings up a bar
 * whose buttons appear only when they apply to something selected - "Issue" for drafts,
 * "Send" for issued invoices with an address.
 */
const InvoicesTab: React.FC<InvoicesTabProps> = ({ kindergartenId, month, counts, onChanged }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const list = useListUrlState({ defaultSort: { sortBy: InvoicesSortKeyEnum.CHILD, sortOrder: SortOrderEnum.ASC } });
  const { search, sortBy, sortOrder, page, perPage } = list;
  const state = list.values(FILTER.state)[0] as InvoiceStateFilterEnum | undefined;
  const groupIds = list.values(FILTER.group);
  const filtered = !!search || !!state || !!groupIds.length;
  const period = parseMonthKey(month);
  const today = toLocalDateString(new Date());

  const [invoices, setInvoices] = useState<InvoiceDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loader, setLoader] = useState(false);
  const beginLoad = useLatestRequest();
  const [groups, setGroups] = useState<{ id: string; name: string }[]>([]);
  const [selected, setSelected] = useState<Map<string, InvoiceDto>>(new Map());
  const [busy, setBusy] = useState(false);
  const [issuing, setIssuing] = useState<InvoiceDto[] | null>(null);
  const [toCancel, setToCancel] = useState<InvoiceDto | null>(null);
  const [toDelete, setToDelete] = useState<InvoiceDto | null>(null);
  // emailing parents cannot be taken back, so every send - a row's or the selection's - is
  // confirmed in the send dialog, which also says who gets each invoice
  const [toSend, setToSend] = useState<{ invoices: InvoiceDto[]; fromSelection: boolean } | null>(null);

  const query = useCallback(
    (extra: { page: number; perPage: number }) =>
      getInvoices(kindergartenId, {
        ...extra,
        periodYear: period?.year,
        periodMonth: period?.month,
        search: search || undefined,
        states: state ? [state] : undefined,
        groupIds: groupIds.length ? groupIds : undefined,
        sortBy: sortBy as InvoicesSortKeyEnum | undefined,
        sortOrder
      }),
    // the group list is rebuilt from the URL on every render; its joined form is the dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [kindergartenId, month, search, state, groupIds.join(), sortBy, sortOrder]
  );

  const load = useCallback(async () => {
    if (!kindergartenId || !period) return;
    const isCurrent = beginLoad();
    setLoader(true);
    try {
      const result = await query({ page, perPage });
      if (!isCurrent()) return;
      setInvoices(result.results ?? []);
      setTotal(result.paginationInfo?.totalItems ?? 0);
    } catch {
      if (isCurrent()) createNotification(MESSAGE_TYPE.ERROR, t('invoices.loadFailed'));
    } finally {
      if (isCurrent()) setLoader(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, page, perPage, t]);

  useEffect(() => {
    load();
  }, [load]);

  // a selection is about one month and one filter; it does not follow either
  useEffect(() => setSelected(new Map()), [month, search, state, groupIds.join()]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getGroupsWithLocationName(kindergartenId)
      .then(result => setGroups((result.results ?? []).map((group: any) => ({ id: group.id, name: group.name }))))
      .catch(() => createNotification(MESSAGE_TYPE.ERROR, t('groups.listLoadFailed')));
  }, [kindergartenId, t]);

  const refresh = () => {
    load();
    onChanged();
  };

  const toggle = (invoice: InvoiceDto) =>
    setSelected(prev => {
      const next = new Map(prev);
      if (next.has(invoice.id)) next.delete(invoice.id);
      else next.set(invoice.id, invoice);
      return next;
    });
  const allOnPage = invoices.length > 0 && invoices.every(invoice => selected.has(invoice.id));
  const togglePage = () =>
    setSelected(prev => {
      const next = new Map(prev);
      if (allOnPage) invoices.forEach(invoice => next.delete(invoice.id));
      else invoices.forEach(invoice => next.set(invoice.id, invoice));
      return next;
    });

  const selectAllInMonth = async () => {
    setBusy(true);
    try {
      const all = new Map<string, InvoiceDto>();
      for (let p = 1; ; p += 1) {
        const result = await query({ page: p, perPage: 100 });
        (result.results ?? []).forEach(invoice => all.set(invoice.id, invoice));
        if (p >= (result.paginationInfo?.totalPages ?? 1)) break;
      }
      setSelected(all);
    } catch {
      createNotification(MESSAGE_TYPE.ERROR, t('invoices.loadFailed'));
    } finally {
      setBusy(false);
    }
  };

  const chosen = useMemo(() => Array.from(selected.values()), [selected]);
  const chosenDrafts = chosen.filter(invoice => invoice.status === InvoiceStatusEnum.DRAFT);
  const chosenSendable = chosen.filter(isSendable);
  const chosenIssued = chosen.filter(isIssued);

  const downloadZip = async (targets: InvoiceDto[]) => {
    setBusy(true);
    try {
      const { blob, skipped } = await downloadInvoicesZip(kindergartenId, targets.map(invoice => invoice.id));
      downloadBlob(blob, `racuni-${month}.zip`);
      if (skipped) createNotification(MESSAGE_TYPE.ERROR, t('invoices.selection.zipSkipped', { count: skipped }));
    } catch {
      createNotification(MESSAGE_TYPE.ERROR, t('invoices.pdf.downloadFailed'));
    } finally {
      setBusy(false);
    }
  };

  const downloadOne = async (invoice: InvoiceDto) => {
    try {
      const result = await getInvoicePdfUrl(kindergartenId, invoice.id);
      if (result.results) await downloadFromPresignedUrl(result.results.url, result.results.fileName);
    } catch (error: any) {
      createNotification(MESSAGE_TYPE.ERROR, error?.response?.data?.message ?? t('invoices.pdf.downloadFailed'));
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await deleteInvoiceDraft(kindergartenId, toDelete.id);
      createNotification(MESSAGE_TYPE.SUCCESS, t('invoices.draft.deleted'));
      setToDelete(null);
      refresh();
    } catch (error: any) {
      createNotification(MESSAGE_TYPE.ERROR, error?.response?.data?.message ?? t('invoices.draft.deleteFailed'));
    }
  };

  const clearFilters = () => list.clearFilters(Object.values(FILTER));

  const stateChips = (
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" role="group" aria-label={t('invoices.columns.state')}>
      {STATE_CHIPS.filter(chip => !chip.value || !counts || counts[chip.count] > 0).map(chip => {
        const active = chip.value === state;
        return (
          <ButtonBase
            key={chip.count}
            aria-pressed={active}
            onClick={() => list.setValues(FILTER.state, chip.value ? [chip.value] : [])}
            sx={{
              height: 32,
              px: 1.5,
              gap: 0.75,
              borderRadius: 999,
              fontSize: 14,
              border: 1,
              borderColor: active ? 'primary.main' : 'field.border',
              bgcolor: active ? 'primary.main' : 'surface.main',
              color: active ? palette.common.white : 'text.primary'
            }}
          >
            {t(`invoices.stateChips.${chip.value ?? 'all'}`)}
            {counts && (
              <Box component="span" sx={{ fontWeight: 600, opacity: active ? 1 : 0.7 }}>
                {counts[chip.count]}
              </Box>
            )}
          </ButtonBase>
        );
      })}
    </Stack>
  );

  return (
    <Box sx={{ border: 1, borderColor: 'line', borderRadius: 4, pb: selected.size ? 10 : 0 }}>
      <ListToolbar
        search={{ value: search, onChange: list.setSearch, placeholder: t('invoices.searchNumberOrChild') }}
        filters={[
          {
            kind: 'multi',
            key: FILTER.group,
            label: t('common.group'),
            options: groups.map(group => ({ value: group.id, label: group.name })),
            selected: groupIds,
            onChange: values => list.setValues(FILTER.group, values)
          }
        ]}
        extra={stateChips}
        resultText={t('finance.allAmountsIn', { currency: DEFAULT_CURRENCY })}
        onClearAll={clearFilters}
      />

      {allOnPage && total > invoices.length && selected.size < total && (
        <Alert severity="info" sx={{ mx: 2.5, mb: 1.5, borderRadius: 2 }} icon={false}>
          {t('invoices.selection.pageSelected', { count: invoices.length })}{' '}
          <ButtonBase onClick={selectAllInMonth} disabled={busy} sx={{ color: 'primary.main', fontSize: 14, fontWeight: 600 }}>
            {t('invoices.selection.selectAllInMonth', { count: total })}
          </ButtonBase>
        </Alert>
      )}

      <BasicTable
        headings={INVOICES_TABLE_HEADINGS}
        loader={loader}
        {...list.tableProps(total)}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortChange={list.setSort}
        emptyState={filtered ? <FilteredEmptyState onClear={clearFilters} /> : undefined}
      >
        {invoices.map(invoice => {
          const remaining = remainingOf(invoice);
          const owed = isIssued(invoice) && remaining > 0;
          const lateBy = owed && invoice.dueDate && invoice.dueDate < today ? daysBetween(invoice.dueDate, today) : 0;
          const open = () => navigate(`${FINANCE}/invoice/${invoice.id}?kindergartenId=${kindergartenId}`, backState(list.here, t('invoices.title')));
          return (
            <TableRow
              key={invoice.id}
              hover
              tabIndex={0}
              selected={selected.has(invoice.id)}
              sx={{ cursor: 'pointer' }}
              onClick={open}
              onKeyDown={event => {
                // Enter on the row's checkbox ticks it; it is not a request to open the invoice
                if (event.target !== event.currentTarget) return;
                if (event.key === 'Enter') open();
              }}
            >
              <TableCell padding="checkbox" onClick={event => event.stopPropagation()}>
                <Checkbox
                  size="small"
                  checked={selected.has(invoice.id)}
                  onChange={() => toggle(invoice)}
                  slotProps={{ input: { 'aria-label': t('invoices.selection.select', { name: invoice.invoiceNumber ?? invoice.childFullName }) } }}
                />
              </TableCell>
              <TableCell>
                {invoice.invoiceNumber ? (
                  <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{invoice.invoiceNumber}</Typography>
                ) : (
                  <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>{t('invoices.draftLabel')}</Typography>
                )}
                {/* the PDF is a small note, not a state of its own */}
                {isIssued(invoice) && invoice.pdfStatus === InvoicePdfStatusEnum.QUEUED && (
                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{t('invoices.pdf.preparing')}</Typography>
                )}
                {isIssued(invoice) && invoice.pdfStatus === InvoicePdfStatusEnum.FAILED && (
                  <Typography sx={{ fontSize: 12, color: 'error.main' }}>{t('invoices.pdf.failedShort')}</Typography>
                )}
              </TableCell>
              <TableCell>
                <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{invoice.childFullName}</Typography>
                {(invoice.groupName || invoice.locationName) && (
                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                    {[invoice.groupName, invoice.locationName].filter(Boolean).join(' · ')}
                  </Typography>
                )}
              </TableCell>
              <TableCell sx={NUMERIC_CELL_SX}>{formatMoney(invoice.totalGross)}</TableCell>
              <TableCell sx={NUMERIC_CELL_SX}>
                {!isIssued(invoice) ? (
                  '—'
                ) : (
                  <>
                    <Box component="span" sx={owed ? { color: 'error.main', fontWeight: 600 } : undefined}>
                      {formatMoney(remaining)}
                    </Box>
                    {invoice.paymentStatus === InvoicePaymentStatusEnum.PARTIALLY_PAID && (
                      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{t('invoices.list.paidAmount', { amount: formatMoney(invoice.paidAmount) })}</Typography>
                    )}
                    {invoice.paymentStatus === InvoicePaymentStatusEnum.PAID && invoice.lastPaymentAt && (
                      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{t('invoices.list.paidOn', { date: formatSerbianDate(invoice.lastPaymentAt) })}</Typography>
                    )}
                  </>
                )}
              </TableCell>
              <TableCell>
                <StatusChip kind="invoiceState" status={invoiceStateOf(invoice)} />
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap', ...(lateBy > 0 ? { color: 'error.main' } : {}) }}>
                {formatSerbianDate(invoice.dueDate)}
                {lateBy > 0 && ` · ${t('invoices.list.lateBy', { count: lateBy })}`}
              </TableCell>
              <TableCell>
                <RowActionsMenu
                  name={invoice.invoiceNumber ?? invoice.childFullName}
                  actions={[
                    { label: t('invoices.pdf.download'), hidden: !invoice.hasPdf, onClick: () => downloadOne(invoice) },
                    {
                      label: invoice.payerEmail ? t('invoices.send.toPayer') : t('invoices.send.toPayerNoAddress'),
                      hidden: !isIssued(invoice),
                      disabled: !invoice.payerEmail,
                      onClick: () => setToSend({ invoices: [invoice], fromSelection: false })
                    },
                    { label: t('invoices.issueDialog.issueOne'), hidden: invoice.status !== InvoiceStatusEnum.DRAFT, onClick: () => setIssuing([invoice]) },
                    { label: t('invoices.cancel.title'), hidden: !isIssued(invoice), danger: true, onClick: () => setToCancel(invoice) },
                    { label: t('invoices.draft.delete'), hidden: invoice.status !== InvoiceStatusEnum.DRAFT, danger: true, onClick: () => setToDelete(invoice) }
                  ]}
                />
              </TableCell>
            </TableRow>
          );
        })}
      </BasicTable>

      {selected.size > 0 && (
        <Box
          role="region"
          aria-label={t('invoices.selection.ariaLabel')}
          sx={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
            bgcolor: 'primary.main',
            color: palette.common.white,
            borderRadius: 4,
            boxShadow: 6,
            px: 2.5,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
        >
          <Typography sx={{ fontSize: 14, color: palette.common.white, mr: 1 }}>{t('invoices.selection.selected', { count: selected.size })}</Typography>
          {[
            chosenDrafts.length > 0 && { key: 'issue', label: t('invoices.selection.issue', { count: chosenDrafts.length }), onClick: () => setIssuing(chosenDrafts) },
            chosenSendable.length > 0 && { key: 'send', label: t('invoices.selection.send', { count: chosenSendable.length }), onClick: () => setToSend({ invoices: chosenSendable, fromSelection: true }) },
            chosenIssued.length > 0 && { key: 'zip', label: t('invoices.selection.zip'), onClick: () => downloadZip(chosenIssued) }
          ]
            .filter((action): action is { key: string; label: string; onClick: () => void } => !!action)
            .map(action => (
              <ButtonBase
                key={action.key}
                onClick={action.onClick}
                disabled={busy}
                sx={{ bgcolor: palette.common.white, color: 'primary.main', borderRadius: 2, px: 1.75, height: 36, fontSize: 14, fontWeight: 500 }}
              >
                {action.label}
              </ButtonBase>
            ))}
          <ButtonBase onClick={togglePage} sx={{ color: palette.common.white, fontSize: 14, px: 1, height: 36, borderRadius: 2, opacity: 0.9 }}>
            {allOnPage ? t('invoices.selection.unselectPage') : t('invoices.selection.selectPage')}
          </ButtonBase>
          <IconButton aria-label={t('invoices.selection.clear')} onClick={() => setSelected(new Map())} sx={{ color: palette.common.white }}>
            <CloseIcon />
          </IconButton>
        </Box>
      )}

      {issuing && (
        <IssueInvoicesDialog
          kindergartenId={kindergartenId}
          drafts={issuing}
          onClose={() => setIssuing(null)}
          onDone={() => {
            setSelected(new Map());
            refresh();
          }}
        />
      )}
      {toCancel && (
        <CancelInvoiceModal
          kindergartenId={kindergartenId}
          invoiceId={toCancel.id}
          invoiceNumber={toCancel.invoiceNumber}
          onClose={() => setToCancel(null)}
          onCancelled={refresh}
        />
      )}
      {toSend && (
        <SendInvoicesDialog
          kindergartenId={kindergartenId}
          invoices={toSend.invoices}
          onClose={() => setToSend(null)}
          onSent={() => {
            if (toSend.fromSelection) setSelected(new Map());
            refresh();
          }}
        />
      )}
      {toDelete && (
        <GenericInfoModal
          open
          onClose={() => setToDelete(null)}
          onConfirm={handleDelete}
          label={t('invoices.draft.delete')}
          message={t('invoices.draft.deleteConfirm')}
          confirmLabel={t('common.delete')}
          entityName={toDelete.childFullName}
        />
      )}
    </Box>
  );
};

export default InvoicesTab;
